import "@/App.css";
import { Toaster } from "sonner";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./components/AuthContext";
import Navigation from "./components/Navigation";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import ServicesSection from "./components/ServicesSection";
import CinematicBreak from "./components/CinematicBreak";
import OurMahiSection from "./components/OurMahiSection";
import TrainingSection from "./components/TrainingSection";
import TrustSecuritySection from "./components/TrustSecuritySection";
import ValuesSection from "./components/ValuesSection";
import TeamSection from "./components/TeamSection";
import AdvisorsSection from "./components/AdvisorsSection";
import MembershipSection from "./components/MembershipSection";
import ContactSection from "./components/ContactSection";
import BlogPreview from "./components/BlogPreview";
import Footer from "./components/Footer";
import { TohuWatermarkFixed } from "./components/TohuWatermark";
import ImpactNumbers from "./components/ImpactNumbers";
import ScrollProgress from "./components/ScrollProgress";
import DomainWorld from "./components/DomainWorld";

// Lazy-loaded routes — only downloaded when navigated to
const AuthPage = lazy(() => import("./components/AuthPage"));
const MemberPortal = lazy(() => import("./components/MemberPortal"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const StaffIntranet = lazy(() => import("./components/StaffIntranet"));
const BlogPage = lazy(() => import("./components/BlogPage").then(m => ({ default: m.BlogList })));
const BlogPostPage = lazy(() => import("./components/BlogPage").then(m => ({ default: m.BlogPost })));
const MembershipRegister = lazy(() => import("./components/MembershipRegister"));
const PartnersPage = lazy(() => import("./components/PartnersPage"));

const valuesMarquee = "Vā Fealoa'i  -  Toi Whakahou  -  Whakamātau  -  Lototō  -  Māia  -  Yalomatua  -  Motutaga Tau Mafola  -  Hihiri  -  ";
const servicesMarquee = "Economic Development  -  Strategy  -  Governance  -  System Design  -  Digital Tools  -  Training  -  Climate Adaptation  -  Research  -  Procurement  -  Infrastructure  -  ";

function MarqueeBand({ text, bg, textColour, direction = "left" }) {
  return (
    <div className={`overflow-hidden py-4`} style={{ background: bg }}>
      <div className={`flex whitespace-nowrap ${direction === "left" ? "animate-marquee" : "animate-marquee-reverse"}`}>
        {[...Array(4)].map((_, i) => (
          <span key={`marquee-${direction}-${i}`} className={`font-['Syne'] text-sm sm:text-base font-bold uppercase tracking-[0.15em] ${textColour} mx-0`}>
            {text}
          </span>
        ))}
      </div>
    </div>
  );
}

function LoadingFallback() {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
      <div className="text-center">
        <p className="font-['Syne'] text-2xl font-bold text-[#bb321a] mb-2">T.I.I.N.A.</p>
        <p className="text-sm text-[#57534E]">Loading...</p>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <>
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ImpactNumbers />

      {/* ===== PEOPLE WORLD ===== */}
      <DomainWorld domain="people">
        <ValuesSection />
        <TeamSection />
        <AdvisorsSection />
        <MembershipSection />
      </DomainWorld>

      {/* People → Place transition */}
      <MarqueeBand text={valuesMarquee} bg="linear-gradient(90deg, #bb321a, #485e23, #364225)" textColour="text-white" direction="left" />
      <MarqueeBand text={servicesMarquee} bg="linear-gradient(90deg, #364225, #485e23, #bb321a)" textColour="text-white/90" direction="right" />

      {/* ===== PLACE WORLD ===== */}
      <DomainWorld domain="place">
        <CinematicBreak />
        <ServicesSection />
      </DomainWorld>

      {/* Our Mahi - between Place and Planet */}
      <OurMahiSection />

      {/* Training & Capability */}
      <TrainingSection />

      {/* Place → Planet transition */}
      <MarqueeBand text="People. Place. Planet.  -  People. Place. Planet.  -  People. Place. Planet.  -  " bg="linear-gradient(90deg, #364225, #45443f, #364225)" textColour="text-white" direction="left" />
      <MarqueeBand text="From the marae to the global boardroom  -  We don't adapt who we are, we advance what's possible  -  " bg="linear-gradient(90deg, #45443f, #bb321a, #45443f)" textColour="text-white/80" direction="right" />

      {/* ===== PLANET WORLD ===== */}
      <DomainWorld domain="planet">
        <BlogPreview />
        <ContactSection />
      </DomainWorld>

      {/* Trust & Security */}
      <TrustSecuritySection />

      <Footer />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <div className="App" style={{ scrollBehavior: "smooth" }}>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#45443f",
              color: "#FDFBF7",
              border: "1px solid rgba(204,85,0,0.2)",
              borderRadius: "0",
              fontFamily: "'Manrope', sans-serif",
            },
          }}
        />
        <BrowserRouter>
          <TohuWatermarkFixed />
          <ScrollProgress />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/portal" element={<MemberPortal />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/staff" element={<StaffIntranet />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:postId" element={<BlogPostPage />} />
              <Route path="/register" element={<MembershipRegister />} />
              <Route path="/partners" element={<PartnersPage />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
